import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { 
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { 
  PieChart, 
  Pie, 
  Cell,
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend
} from 'recharts';
import DatePicker from 'react-datepicker';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import api, { endpoints } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import "react-datepicker/dist/react-datepicker.css";

const AIModelDistribution = () => {
  const [loading, setLoading] = useState(true);
  const [requestDistribution, setRequestDistribution] = useState(null);
  const [teams, setTeams] = useState([]);
  const [dailyModelUsage, setDailyModelUsage] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loadingDistribution, setLoadingDistribution] = useState(true);
  const [loadingModelUsage, setLoadingModelUsage] = useState(true);

  // Initialize dates - default to 14 days for optimal performance
  const [dateRange, setDateRange] = useState(() => ({
    startDate: startOfDay(subDays(new Date(), 14)),
    endDate: endOfDay(new Date())
  }));

  // Filter states
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [includeIT, setIncludeIT] = useState(false);

  // Chart height based on viewport
  const getChartHeight = () => {
    const width = window.innerWidth;
    if (width < 640) return 220; // Mobile
    if (width < 1024) return 280; // Tablet
    return 350; // Desktop
  };

  // Color palettes
  const COLORS = ['#4E79A7', '#F28E2B', '#E15759', '#76B7B2', '#59A14F'];

  const MODEL_PALETTE = [
    '#4E79A7', // blue
    '#F28E2B', // orange
    '#E15759', // red
    '#76B7B2', // teal
    '#59A14F', // green
    '#EDC948', // yellow
    '#B07AA1', // purple
    '#FF9DA7', // pink
    '#9C755F', // brown
    '#BAB0AC'  // gray
  ];

  const normalizeModelName = (name) => {
    return (name || 'Unknown').trim().toLowerCase();
  };

  // Consistent color mapping for model names across charts
  const getModelColor = (name, fallbackIndex = 0) => {
    const n = normalizeModelName(name);
    if (n === 'other' || n.includes('other')) return '#BAB0AC'; // consistent grey for Other

    // Specific mappings for popular models/families
    if (n === 'default') return '#8B5CF6'; // vivid violet for generic/default bucket
    if ((n.includes('claude-4') || (n.includes('claude') && n.includes('4'))) && n.includes('thinking')) return '#E15759'; // red for Claude 4 thinking
    if (n.includes('claude') && (n.includes('-4') || n.includes(' 4'))) return '#EDC948'; // gold for Claude 4
    if (n.includes('gpt-4o') || n.includes('gpt-4.1') || n.includes('gpt-4')) return '#4E79A7'; // blue for GPT-4 family
    if (n.includes('gpt-3.5')) return '#B07AA1'; // purple for GPT-3.5
    if (n.includes('llama')) return '#59A14F'; // green for Llama
    if (n.includes('gemini')) return '#76B7B2'; // teal for Gemini
    if (n.includes('mistral')) return '#E15759'; // red for Mistral
    if (n.includes('deepseek')) return '#9C755F'; // brown for DeepSeek
    if (n.includes('qwen')) return '#FF9DA7'; // pink for Qwen

    // Fallback to a stable palette index
    return MODEL_PALETTE[fallbackIndex % MODEL_PALETTE.length];
  };

  // Transform backend data into request types distribution for pie chart
  const getRequestTypesData = () => {
    if (!requestDistribution?.requestTypes) return [];
    
    const overview = requestDistribution.requestTypes;
    const requestTypes = [
      { name: 'Composer Requests', value: overview.totalComposerRequests || 0 },
      { name: 'Chat Requests', value: overview.totalChatRequests || 0 },
      { name: 'Agent Requests', value: overview.totalAgentRequests || 0 },
      { name: 'Cmd+K Usage', value: overview.totalCmdKUsages || 0 }
    ].filter(item => item.value > 0) // Only show types with usage
     .sort((a, b) => b.value - a.value); // Sort by usage descending
    
    return requestTypes;
  };

  // Aggregate AI models data for pie chart - show top 5 + Other
  const getAIModelsDistribution = () => {
    if (!requestDistribution?.topModels || !Array.isArray(requestDistribution.topModels)) return [];
    
    // Use topModels data directly - represents actual API request counts
    const allModels = requestDistribution.topModels
      .map(item => ({
        name: item.model || 'Unknown',
        value: item.count || 0,
        percentage: 0 // Will be calculated by recharts
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value); // Sort by usage count descending

    // If 5 or fewer models, return all
    if (allModels.length <= 5) {
      return allModels;
    }

    // Take top 5 and group the rest into "Other"
    const top5 = allModels.slice(0, 5);
    const others = allModels.slice(5);
    const otherTotal = others.reduce((sum, item) => sum + item.value, 0);

    if (otherTotal > 0) {
      return [...top5, { name: 'Other', value: otherTotal, percentage: 0 }];
    }

    return top5;
  };

  // ----- Model Usage Trend helpers -----
  const ClampedTooltipContent = ({ active, payload, label, seriesOrder }) => {
    if (!active || !payload || payload.length === 0) return null;
    const items = Array.isArray(payload) ? [...payload] : [];
    if (Array.isArray(seriesOrder) && seriesOrder.length > 0) {
      const orderMap = new Map(seriesOrder.map((name, idx) => [name, idx]));
      items.sort((a, b) => {
        const ai = orderMap.has(a.name) ? orderMap.get(a.name) : Number.POSITIVE_INFINITY;
        const bi = orderMap.has(b.name) ? orderMap.get(b.name) : Number.POSITIVE_INFINITY;
        return bi - ai;
      });
    }
    return (
      <div style={{
        backgroundColor: '#fff',
        padding: '10px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        maxWidth: 360
      }}>
        <p style={{ margin: '0 0 5px', fontWeight: 'bold' }}>{`Date: ${label}`}</p>
        {items.map((entry, index) => (
          <p key={`item-${index}`} style={{ margin: '2px 0', color: entry.color }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  };

  const buildModelStackFromRows = (rows, inputTopModels = [], topN = 4, padRange) => {
    const dataRows = Array.isArray(rows) ? rows : [];
    const providedDisplay = new Map();
    (inputTopModels || []).forEach((m) => {
      const norm = normalizeModelName(m);
      if (!providedDisplay.has(norm)) providedDisplay.set(norm, m);
    });
    const totals = new Map();
    const seenDisplay = new Map();
    for (const r of dataRows) {
      const norm = normalizeModelName(r?.model);
      const disp = r?.model || 'Unknown';
      if (!seenDisplay.has(norm)) seenDisplay.set(norm, disp);
      totals.set(norm, (totals.get(norm) || 0) + Number(r?.events || 0));
    }
    const topNorm = (Array.isArray(inputTopModels) && inputTopModels.length > 0)
      ? inputTopModels.map(normalizeModelName).slice(0, topN)
      : Array.from(totals.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, topN)
          .map(([norm]) => norm);
    const labelFor = (norm) => providedDisplay.get(norm) || seenDisplay.get(norm) || norm;
    const byDate = new Map();
    for (const r of dataRows) {
      const raw = r?.date || r?.EventDate || r?.Date;
      let d = '';
      if (raw instanceof Date) d = raw.toISOString().slice(0, 10);
      else if (typeof raw === 'string') d = raw.slice(0, 10);
      if (!d) continue;
      const norm = normalizeModelName(r?.model);
      const bucket = topNorm.includes(norm) ? norm : 'other';
      const ev = Number(r?.events || 0);
      const row = byDate.get(d) || { date: d };
      row[bucket] = (row[bucket] || 0) + ev;
      byDate.set(d, row);
    }
    const series = [...topNorm.map(labelFor), 'Other'];
    const chartData = Array.from(byDate.values())
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((row) => {
        const out = { date: row.date };
        for (const n of topNorm) {
          const disp = labelFor(n);
          out[disp] = row[n] || 0;
        }
        out['Other'] = row['other'] || 0;
        return out;
      });

    // Pad missing dates with zeros
    if ((padRange && padRange.startDate && padRange.endDate) || chartData.length > 0) {
      const startStr = padRange?.startDate || chartData[0]?.date;
      const endStr = padRange?.endDate || chartData[chartData.length - 1]?.date;
      if (startStr && endStr) {
        const byDateFull = new Map(chartData.map((r) => [r.date, r]));
        const zeroTemplate = {};
        for (const s of series) zeroTemplate[s] = 0;

        const start = new Date(`${String(startStr).slice(0, 10)}T12:00:00`);
        const end = new Date(`${String(endStr).slice(0, 10)}T12:00:00`);

        const padded = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const key = format(d, 'yyyy-MM-dd');
          const row = byDateFull.get(key) || { date: key, ...zeroTemplate };
          padded.push(row);
        }
        return { series, chartData: padded };
      }
    }

    return { series, chartData };
  };

  const ModelUsageTrendChart = ({ data, topModels = [] }) => {
    const containerRef = useRef(null);
    const [tooltipPos, setTooltipPos] = useState(null);

    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center h-48">
          <p className="text-secondary-500">No model usage data available</p>
        </div>
      );
    }

    let series = [];
    let formattedChartData = [];
    try {
      const stack = buildModelStackFromRows(
        data,
        topModels,
        4,
        kpis?.dateRange
      );
      series = stack.series;
      formattedChartData = stack.chartData.map((item) => {
        try {
          const dateOnly = typeof item.date === 'string' ? item.date.slice(0, 10) : item.date;
          const dateObj = typeof dateOnly === 'string' ? new Date(`${dateOnly}T12:00:00`) : dateOnly;
          return { ...item, formattedDate: format(dateObj, 'MMM dd') };
        } catch (e) {
          return { ...item, formattedDate: String(item.date).slice(0, 10) };
        }
      });
    } catch (e) {
      console.error('Error preparing ModelUsageTrend data:', e);
      return (
        <div className="flex items-center justify-center h-48">
          <p className="text-red-500">Error displaying model usage data</p>
        </div>
      );
    }

    const computeAndSetTooltipPos = (e) => {
      if (!containerRef.current || !e) return;
      const rect = containerRef.current.getBoundingClientRect();
      const containerWidth = rect.width;
      const containerHeight = rect.height;
      const approxWidth = 340;
      const lines = e.activePayload?.length || 1;
      const approxHeight = 32 + lines * 20 + 12;
      let x = (e.chartX || 0) + 12;
      let y = (e.chartY || 0) - approxHeight - 12;
      if (x + approxWidth > containerWidth) x = containerWidth - approxWidth - 8;
      if (x < 8) x = 8;
      if (y < 8) y = Math.min(containerHeight - approxHeight - 8, (e.chartY || 0) + 12);
      if (y < 8) y = 8;
      setTooltipPos({ x: Math.round(x), y: Math.round(y) });
    };

    return (
      <div ref={containerRef} style={{ width: '100%', height: getChartHeight() }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={formattedChartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            onMouseMove={computeAndSetTooltipPos}
            onMouseLeave={() => setTooltipPos(null)}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="formattedDate" tick={{ fontSize: 12 }} tickLine={false} />
            <YAxis label={{ value: 'Usage Count', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }} tick={{ fontSize: 12 }} />
            <Tooltip
              content={(props) => <ClampedTooltipContent {...props} seriesOrder={series} />}
              position={tooltipPos || undefined}
              wrapperStyle={{ pointerEvents: 'none' }}
              allowEscapeViewBox={{ x: true, y: true }}
              cursor={{ strokeDasharray: '3 3' }}
            />
            <Legend verticalAlign="bottom" height={36} iconType="circle" />
            {series.map((name, i) => (
              <Bar key={name} dataKey={name} stackId="models" fill={getModelColor(name, i)} name={name} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Load data function
  const loadAnalyticsData = useCallback(async () => {
    if (!dateRange.startDate || !dateRange.endDate) return;

    setLoadingDistribution(true);
    setLoadingModelUsage(true);
    
    try {
      const params = new URLSearchParams({
        startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
        endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
        team: selectedTeam === 'all' ? '' : selectedTeam,
        includeIT: includeIT.toString()
      });

      // Fetch distribution data and model usage data in parallel
      const distributionPromise = api.get(`${endpoints.analytics.requestDistribution}?${params.toString()}`)
        .then(res => {
          setRequestDistribution(res.data);
          // Also fetch teams list if not loaded
          if (teams.length === 0) {
            api.get(endpoints.analytics.teams).then(res => setTeams(res.data.teams || []));
          }
        }).finally(() => setLoadingDistribution(false));

      const modelUsagePromise = api.get(`/analytics/daily-model-usage?${params.toString()}`)
        .then(res => setDailyModelUsage(res.data))
        .finally(() => setLoadingModelUsage(false));

      const kpisPromise = api.get('/analytics/kpis', { params: Object.fromEntries(params) })
        .then(res => setKpis(res.data));

      await Promise.all([distributionPromise, modelUsagePromise, kpisPromise]);

    } catch (error) {
      console.error('Failed to load analytics data:', error);
      toast.error('Failed to load analytics data');
      setLoadingDistribution(false);
      setLoadingModelUsage(false);
    } finally {
      setLoading(false); 
    }
  }, [selectedTeam, dateRange, includeIT, teams.length]);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      loadAnalyticsData();
    }
  }, [loadAnalyticsData, dateRange]);

  const handleDateRangeChange = (field, date) => {
    const newRange = {
      ...dateRange,
      [field]: date
    };

    // Validate date range doesn't exceed 30 days
    if (newRange.startDate && newRange.endDate) {
      const daysDiff = Math.ceil((newRange.endDate - newRange.startDate) / (1000 * 60 * 60 * 24));
      if (daysDiff > 30) {
        toast.error('Date range cannot exceed 30 days for optimal performance');
        return;
      }
    }

    setDateRange(newRange);
  };

  const handleQuickDateRange = (days) => {
    setDateRange({
      startDate: startOfDay(subDays(new Date(), days)),
      endDate: endOfDay(new Date())
    });
    
    // Trigger data reload immediately for quick date selections
    setTimeout(() => loadAnalyticsData(), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900 flex items-center gap-3">
            <BarChart3 className="text-primary-600" size={32} />
            AI Model Distribution
          </h1>
          <p className="mt-1 text-sm text-secondary-600">
            Analyze request types and AI model usage patterns
          </p>
        </div>
        <button
          onClick={loadAnalyticsData}
          className="btn btn-secondary flex items-center gap-2 self-start sm:self-center"
          disabled={loadingDistribution}
        >
          <RefreshCw className={`w-4 h-4 ${loadingDistribution ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Date Range
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <DatePicker
                  selected={dateRange.startDate}
                  onChange={(date) => handleDateRangeChange('startDate', date)}
                  selectsStart
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  dateFormat="MMM d, yyyy"
                  className="input w-full"
                  placeholderText="Start Date"
                />
                <span className="hidden sm:flex items-center text-secondary-500">to</span>
                <DatePicker
                  selected={dateRange.endDate}
                  onChange={(date) => handleDateRangeChange('endDate', date)}
                  selectsEnd
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  minDate={dateRange.startDate}
                  dateFormat="MMM d, yyyy"
                  className="input w-full"
                  placeholderText="End Date"
                />
              </div>
            </div>

            {/* Quick Select */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Quick Select
              </label>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleQuickDateRange(7)}
                    className="btn btn-secondary flex-1 text-sm"
                  >
                    7d
                  </button>
                  <button
                    onClick={() => handleQuickDateRange(14)}
                    className="btn btn-secondary flex-1 text-sm"
                  >
                    14d
                  </button>
                  <button
                    onClick={() => handleQuickDateRange(30)}
                    className="btn btn-secondary flex-1 text-sm"
                  >
                    30d
                  </button>
                </div>
                <p className="text-xs text-secondary-500">
                  Max: 30 days for optimal performance
                </p>
              </div>
            </div>

            {/* Team Filter */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Team
              </label>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="input w-full"
              >
                <option value="all">All Teams</option>
                {teams.map(team => (
                  <option key={team.teamId} value={team.teamId}>
                    {team.teamName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Include IT Toggle */}
          <div className="mt-4 flex items-center">
            <input
              type="checkbox"
              id="includeIT"
              checked={includeIT}
              onChange={(e) => setIncludeIT(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
            />
            <label htmlFor="includeIT" className="ml-2 block text-sm text-secondary-700">
              Include IT/Support Teams
            </label>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Request Types Distribution Chart */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-secondary-900">
              Request Types Distribution
            </h3>
            <p className="text-sm text-secondary-600">
              Breakdown of Cursor AI request types by total usage count
            </p>
          </div>
          <div className="card-body">
            {loadingDistribution ? (
              <div className="flex items-center justify-center" style={{ height: getChartHeight() }}>
                <LoadingSpinner />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={getChartHeight()}>
                <PieChart>
                  <Pie
                    data={getRequestTypesData()}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, value, percent }) => 
                      `${name}: ${(percent * 100).toFixed(1)}%`
                    }
                    labelLine={false}
                  >
                    {getRequestTypesData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [`${value} requests`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* AI Models Used by Developers */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-secondary-900">
              AI Models Distribution
            </h3>
            <p className="text-sm text-secondary-600">
              Distribution of AI models by total API request count
            </p>
          </div>
          <div className="card-body">
            {loadingDistribution ? (
              <div className="flex items-center justify-center" style={{ height: getChartHeight() }}>
                <LoadingSpinner />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={getChartHeight()}>
                {getAIModelsDistribution().length > 0 ? (
                  <PieChart>
                    <Pie
                      data={getAIModelsDistribution()}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, value, percent }) => 
                        `${name}: ${value} (${(percent * 100).toFixed(1)}%)`
                      }
                      labelLine={false}
                    >
                      {getAIModelsDistribution().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getModelColor(entry.name, index)} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [`${value} requests`, name]}
                    />
                  </PieChart>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-secondary-500">No model usage data available</p>
                  </div>
                )}
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Model Usage Trend (Daily) */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-secondary-900">Model Usage Trend (Daily)</h3>
          <p className="text-sm text-secondary-600">Stacked by top models across the selected scope</p>
        </div>
        <div className="card-body">
          {loadingModelUsage ? (
            <div className="flex items-center justify-center" style={{ height: getChartHeight() }}>
              <LoadingSpinner />
            </div>
          ) : (
            <ModelUsageTrendChart 
              data={dailyModelUsage}
              topModels={(requestDistribution?.topModels || []).map((m) => m.model)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AIModelDistribution;

